import { Button, Icon, OsStack, Typography } from "@outschool/backpack";
import { faArrowLeft, faCalendar } from "@outschool/icons";
import { useLocalStorageState } from "@outschool/local-storage";
import { useTranslation } from "@outschool/localization";
import { dayjs, pastDurationStringWithTimes } from "@outschool/time";
import { ErrorMessage, useFeatureFlag } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import React, { useMemo } from "react";

import { useMuxClassRecordingQuery } from "../../../hooks/useMuxClassroomRecording";
import ClassRecordingsPlayer from "../../ClassRecordingsPlayer";
import { ClassRecordingTabs } from "./ClassRecordingTabs";
import TermsAcceptance from "./TermsAcceptance";

const TERMS_ACCEPTED_AT_KEY = "view_class_recording_terms_accepted_at";
const RECORDING_DURATION_LIMIT_MINUTES = 60 * 3; // 180 minutes

interface ClassRecordingContentProps
  extends Omit<ReturnType<typeof useMuxClassRecordingQuery>, "usersCanPost"> {
  recordingUid: string;
  sectionUid: string;
  userTimeZone: string;
  isLearner: boolean;
  handleBackClick: () => void;
  track: (
    event: string,
    properties?: {
      [propName: string]: any;
    },
  ) => void;
  handlePlayClick?: () => void;
}

export default function ClassRecordingContent({
  recordingUid,
  sectionUid,
  section,
  activity,
  meeting,
  startTime,
  endTime,
  canManage,
  loading,
  error,
  isExpired,
  learnersForParentInSection,
  userTimeZone,
  isLearner,
  handleBackClick,
  handlePlayClick,
  track,
}: ClassRecordingContentProps) {
  // This component is used in learner app (RecordingPage.tsx) and website (MuxRecordingPage.tsx)
  // The learner app does not support translations, but website does
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\classRecording\\ClassRecordingContent",
  );
  const classroomRedesignEnabled = useFeatureFlag(
    "aic-classroom-foyer-redesign",
  );

  const [termsAcceptedAt, setTermsAcceptedAt] = useLocalStorageState<
    string | null
  >(TERMS_ACCEPTED_AT_KEY, null);
  const termsAcceptedInLast24Hours = React.useMemo(
    () =>
      termsAcceptedAt !== null &&
      dayjs(termsAcceptedAt).isAfter(dayjs().subtract(24, "hours")),
    [termsAcceptedAt],
  );

  const handleTermsAccepted = () => {
    if (termsAcceptedInLast24Hours) {
      return;
    }
    track("accept_recording_terms", {
      activityUid: activity?.uid,
      sectionUid: section?.uid,
      recordingId: recordingUid,
    });
    setTermsAcceptedAt(new Date().toISOString());
  };

  const hasExceededRecordingDurationLimit = useMemo(() => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const duration = dayjs.duration(end.diff(start)).asMinutes();
    return duration > RECORDING_DURATION_LIMIT_MINUTES;
  }, [startTime, endTime]);

  if (loading) {
    return <Loading />;
  }

  if (error || isExpired) {
    const errorMessage =
      error?.message ||
      t`This recording is no longer available.  Outschool retains recordings for three months.`;
    return <ErrorMessage value={errorMessage} />;
  }

  if (!section) {
    OsPlatform.captureError("Recording page loaded but section is not defined");
    return (
      <ErrorMessage value="Oops, we encountered an error when loading this recording." />
    );
  }

  // Only show class recording tabs for parents/teachers (not learners)
  const showClassRecordingTabs =
    Boolean(classroomRedesignEnabled) &&
    !isLearner &&
    Boolean(meeting) &&
    Boolean(meeting?.uid);

  const learnerUids = (learnersForParentInSection ?? [])
    .map((l: { uid: string } | null) => l?.uid)
    .filter((uid: string | undefined): uid is string => uid != null);

  return (
    <OsStack direction="column" spacing={6}>
      <PrimaryContent
        termsAcceptedInLast24Hours={termsAcceptedInLast24Hours}
        hasExceededRecordingDurationLimit={hasExceededRecordingDurationLimit}
        sectionUid={sectionUid}
        recordingUid={recordingUid}
        handleTermsAccepted={handleTermsAccepted}
        handlePlayClick={handlePlayClick}
        canManage={canManage}
        learnerUids={learnerUids}
      />
      <Button
        variant="link"
        startIcon={<Icon icon={faArrowLeft} />}
        onClick={handleBackClick}
        sx={{ alignSelf: "flex-start" }}
      >
        {t("Back to classroom")}
      </Button>
      <OsStack direction="column" alignItems="flex-start" spacing={2}>
        <Typography variant="h3">{activity?.title}</Typography>
        <Typography
          variant="body1"
          color="neutral.500"
          sx={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          <Icon icon={faCalendar} size="small" />
          {Boolean(startTime) &&
            Boolean(endTime) &&
            pastDurationStringWithTimes(startTime, endTime, userTimeZone)}
        </Typography>
      </OsStack>
      {showClassRecordingTabs && (
        <ClassRecordingTabs meetingUid={meeting!.uid} canManage={canManage} />
      )}
    </OsStack>
  );
}

interface PrimaryContentProps {
  termsAcceptedInLast24Hours: boolean;
  hasExceededRecordingDurationLimit: boolean;
  sectionUid: string;
  recordingUid: string;
  handleTermsAccepted: () => void;
  handlePlayClick?: () => void;
  canManage?: boolean;
  learnerUids: string[];
}

// Primary content is either TermsAcceptance, an ErrorMessage, or video player
function PrimaryContent({
  termsAcceptedInLast24Hours,
  hasExceededRecordingDurationLimit,
  sectionUid,
  recordingUid,
  handleTermsAccepted,
  handlePlayClick,
  canManage,
  learnerUids,
}: PrimaryContentProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\classRecording\\ClassRecordingContent",
  );

  if (!termsAcceptedInLast24Hours) {
    return (
      <TermsAcceptance
        handleTermsAccepted={handleTermsAccepted}
        canManage={canManage}
      />
    );
  }
  if (hasExceededRecordingDurationLimit) {
    return (
      <ErrorMessage
        value={t(
          "Sorry, this recording is over {{numMinutes}} minutes and cannot be displayed.",
          { numMinutes: RECORDING_DURATION_LIMIT_MINUTES },
        )}
      />
    );
  }
  return (
    <ClassRecordingsPlayer
      sectionUid={sectionUid}
      recordingUid={recordingUid}
      handlePlayClick={handlePlayClick}
      learnerUids={learnerUids}
    />
  );
}
